import { SerializedConstructor } from '@langchain/core/load/serializable';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  LinearProgress,
  Stack,
} from '@mui/joy';

import { JSONSchema7 } from 'json-schema';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';

import {
  resolveRef,
  resolveSchemaComposition,
} from '@/Pages/Graph/src/json-schema-utils';
import {
  CHAT_MODEL_MAPPING,
  TEXT_MODEL_MAPPING,
  getModelDisplayNameFromIdList,
} from '@/Pages/Playground/Playground.constants';
import {
  KwargsSerializedConstructor,
  ManifestEditorProps,
  ManifestPreviewProps,
  ManifestVariant,
  tableVariantContainerClass,
  tableVariantLabelClass,
  tableVariantRowClass,
} from '@/Pages/Playground/components/manifest/Manifest.utils';
import { ManifestProviderReplaceWithSwitch } from '@/Pages/Playground/components/manifest/ManifestSelectProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { prepareUrl } from '@/data/fetcher.tsx';
import { getAuthHeaders } from '@/data/getAuthHeaders';
import ExternalLinkIcon from '@/icons/ExternalLinkIcon.svg?react';
import { getPlaygroundPath } from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { InstructModelBadge } from '../../../InstructModelBadge';
import { ManifestSelectModel } from '../../../ManifestSelectModel';

function ManifestConfigProperty(props: {
  schema: JSONSchema7;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (
    props.schema.type === 'string' &&
    Array.isArray(props.schema.enum) &&
    props.schema.enum.length > 0
  ) {
    return (
      <Select
        value={props.value != null ? String(props.value) : ''}
        onValueChange={(value) => props.onChange(value)}
      >
        <SelectTrigger className="text-md [&_svg]:mr-0.5 [&_svg]:h-5 [&_svg]:w-5">
          <SelectValue placeholder={props.schema.title} />
        </SelectTrigger>
        <SelectContent>
          {props.schema.enum
            .filter((x): x is string => typeof x === 'string')
            .map((value: string, i) => (
              <SelectItem key={i} value={value}>
                {value}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    );
  }

  if (props.schema.type === 'number' || props.schema.type === 'integer') {
    return (
      <Input
        type="number"
        value={props.value != null ? String(props.value) : ''}
        onChange={(e) => props.onChange(e.target.value)}
        sx={{ '& input': { boxShadow: 'none !important' } }}
      />
    );
  }

  if (props.schema.type === 'boolean') {
    return (
      <Select
        value={props.value ? 'true' : 'false'}
        onValueChange={(value) => props.onChange(value === 'true')}
      >
        <SelectTrigger className="text-md [&_svg]:mr-0.5 [&_svg]:h-5 [&_svg]:w-5">
          <SelectValue placeholder={props.schema.title} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type="text"
      value={props.value != null ? String(props.value) : ''}
      onChange={(e) => props.onChange(e.target.value)}
      sx={{ '& input': { boxShadow: 'none !important' } }}
    />
  );
}

function ManifestCustomModelConfig(props: {
  url: string;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  readOnly?: boolean;
  variant?: ManifestVariant;
}) {
  const [debouncedUrl] = useDebounce(props.url, 500, { trailing: true });
  const configUrl = prepareUrl(
    `${getPlaygroundPath(false)}/remote/config_schema`,
    new URLSearchParams()
  );
  const schema = useSWR(
    ['playground', 'remote', debouncedUrl],
    async ([, , url]) => {
      const response = await fetch(configUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders()),
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) throw new Error('Failed to fetch config schema');
      return (await response.json()) as JSONSchema7;
    },
    { errorRetryCount: 0 }
  );

  // resolve configurable
  const configurableSchema =
    schema.data?.type === 'object' &&
    typeof schema.data.properties?.['configurable'] !== 'boolean'
      ? resolveRef(schema.data, schema.data.properties?.['configurable']?.$ref)
      : null;

  // render configurable objects
  if (!configurableSchema && schema.isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  if (!configurableSchema) return null;

  return (
    <ErrorBoundary>
      {configurableSchema.properties &&
        Object.entries(configurableSchema.properties).map(([key, rawValue]) => {
          const propSchema = resolveSchemaComposition(rawValue, schema.data);
          if (propSchema == null) return null;

          if (props.variant === 'table') {
            if (!props.value[key]) return null;
            return (
              <div className={tableVariantRowClass}>
                <div className={tableVariantLabelClass}>
                  {propSchema.title ?? key}
                </div>
                <div>{JSON.stringify(props.value[key]) ?? ''}</div>
              </div>
            );
          }

          return (
            <FormControl key={key}>
              <FormLabel>{propSchema.title ?? key}</FormLabel>

              <ManifestConfigProperty
                schema={propSchema}
                value={
                  props.value.configurable
                    ? props.value.configurable[key]
                    : null
                }
                onChange={(value) => {
                  props.onChange({
                    ...(props.value.configurable
                      ? props.value.configurable
                      : {}),
                    [key]: value,
                  });
                }}
              />

              <FormHelperText>{propSchema.description}</FormHelperText>
            </FormControl>
          );
        })}
    </ErrorBoundary>
  );
}

export const ManifestCustomModel = (
  props: ManifestEditorProps<
    KwargsSerializedConstructor<{
      url?: string;
      configurable?: Record<string, unknown>;
    }>
  >
) => {
  const url = props.value.kwargs['url'] ?? '';

  const path = useMemo(() => {
    try {
      return new URL(url).pathname;
    } catch {
      return '/';
    }
  }, [url]);

  const [isChatModel, setIsChatModel] = useState<boolean>(true);

  return (
    <div
      className={cn(
        'flex flex-col gap-3',
        props.variant === 'table' && tableVariantContainerClass
      )}
    >
      <ManifestProviderReplaceWithSwitch
        variant={props.variant}
        onChange={(value) => {
          setIsChatModel(!isChatModel);
          props.onChange(value);
        }}
        value={props.value}
        readOnly={props.readOnly}
        isChatModel={isChatModel}
        otherModelId={
          isChatModel
            ? TEXT_MODEL_MAPPING['CustomModel'].modelId
            : CHAT_MODEL_MAPPING['ChatCustomModel'].modelId
        }
      />

      {props.variant === 'table' && props.readOnly ? (
        <div className={tableVariantRowClass}>
          <div className={tableVariantLabelClass}>URL</div>
          <div className="max-w-[500px] truncate">{url}</div>
        </div>
      ) : (
        <FormControl>
          <FormLabel>URL</FormLabel>
          <Input
            type="text"
            value={url}
            placeholder="http://your.custom-model.host"
            onChange={(e) => {
              props.onChange({
                ...props.value,
                kwargs: {
                  ...props.value.kwargs,
                  url: e.target.value,
                },
              });
            }}
            sx={{ '& input': { boxShadow: 'none !important' } }}
          />
          <FormHelperText>
            <span>
              The URL must be accessible from the LangSmith Playground service.{' '}
              <a
                href="https://docs.langchain.com/langsmith/custom-endpoint"
                target="_blank"
                className="inline-flex items-center gap-1 text-brand-green-400 hover:underline"
              >
                <span>Learn more</span>
                <ExternalLinkIcon className="h-4 w-4" />
              </a>
            </span>
          </FormHelperText>
        </FormControl>
      )}

      <ManifestSelectModel
        models={[]}
        dynamic={{
          id: props.value.id,
          secrets: { url },
        }}
        value={path}
        onChange={(model) => {
          props.onChange({
            ...props.value,
            kwargs: {
              ...props.value.kwargs,
              url: (() => {
                try {
                  return new URL(model, url).toString();
                } catch {
                  if (url.endsWith('/') && model.startsWith('/'))
                    return url + model.substring(1);
                  if (!url.endsWith('/') && !model.startsWith('/'))
                    return url + '/' + model;
                  return url + model;
                }
              })(),
            },
          });
        }}
        variant={props.variant}
        readOnly={props.readOnly}
      />

      <ManifestCustomModelConfig
        url={props.value.kwargs['url'] ?? ''}
        value={props.value.kwargs}
        onChange={(configurable) => {
          props.onChange({
            ...props.value,
            kwargs: {
              ...props.value.kwargs,
              configurable,
            },
          });
        }}
        variant={props.variant}
      />
    </div>
  );
};

export function ManifestChatCustomModelPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model;

  if (props.variant === 'line') {
    return (
      <Stack direction={'row'} gap={0.5}>
        <span className="font-semibold">{displayLabel}</span>
        {model && <span>{model}</span>}
      </Stack>
    );
  }

  return null;
}

export function ManifestCustomModelPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row gap-1">
        <span className="my-auto font-semibold">{displayLabel}</span>
        {model && <span>{model}</span>}
        <InstructModelBadge />
      </div>
    );
  }

  return null;
}
